import { formatCount, isRoleColor, roleColorLabel, tt } from '@agos/shared'

import type { RoleDelegationEdge } from '../../roles/roles-api'
import type { AuditEntry } from '../permission-matrix'

/**
 * What one trail entry says beyond *who did what to whom* - `FRONTEND.md` §9.752's *História* (design §2.9).
 *
 * The trail stores two snapshots whole (`permission_audit_log.before` / `after`, §10A.408), and their shape depends
 * on the action. `auditChange` already reads the two that were there before O119 - a role's rights and a person's
 * exceptions; this reads the new ones the screen draws in words: a person moved *z Čašník na Barman* (and whether that
 * signed them out), and the delegation edges a save added and removed. **Read, not trusted**: a snapshot that is not
 * what the action implies reads as nothing, because an older entry or a hand-written row must not take the trail down.
 *
 * The action's own words are `actionText`'s, which the backend lane extends with the five new actions; this file does
 * not spell them a second time.
 */

function record(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

function text(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null
}

/** A role as a snapshot names it: its own name, else the label of its key, else of its base. */
function snapshotRole(snapshot: Record<string, unknown> | null, nameOf: (key: string) => string): string | null {
  if (snapshot === null) return null
  const name = text(snapshot.roleName)
  if (name !== null) return name
  const key = text(snapshot.roleKey) ?? text(snapshot.systemRole)
  return key === null ? null : nameOf(key)
}

export interface RoleMove {
  from: string
  to: string
  signedOut: boolean
}

/**
 * An `assign_role` entry as *z {from} na {to}* - `{roleKey, roleName, systemRole}` before and after, and `signedOut`
 * after (§10A.408's table). Null for any other entry, or one whose snapshots do not name a role.
 */
export function roleMoveOf(entry: AuditEntry, nameOf: (key: string) => string): RoleMove | null {
  if (entry.action !== 'assign_role') return null
  const before = record(entry.before)
  const after = record(entry.after)
  const from = snapshotRole(before, nameOf)
  const to = snapshotRole(after, nameOf)
  if (from === null || to === null) return null
  return { from, to, signedOut: after?.signedOut === true }
}

function edgesIn(snapshot: unknown): RoleDelegationEdge[] {
  const value = record(snapshot)
  const list = value === null || !Array.isArray(value.edges) ? [] : value.edges
  return list
    .map(record)
    .filter((edge): edge is Record<string, unknown> => edge !== null)
    .filter((edge) => typeof edge.manager === 'string' && typeof edge.target === 'string')
    .map((edge) => ({
      manager: edge.manager as string,
      target: edge.target as string,
      assign: edge.assign === true,
      edit: edge.edit === true,
    }))
    .filter((edge) => edge.assign || edge.edit)
}

function edgeId(edge: RoleDelegationEdge): string {
  return `${edge.manager}>${edge.target}:${edge.assign ? 'a' : ''}${edge.edit ? 'e' : ''}`
}

/**
 * A `replace_delegations` entry as the edges it added and removed. An edge whose verbs changed reads as removed in
 * its old form and added in its new one, which is what it is. Null for any other entry.
 */
export function edgeChangesOf(entry: AuditEntry): { added: RoleDelegationEdge[]; removed: RoleDelegationEdge[] } | null {
  if (entry.action !== 'replace_delegations') return null
  const before = edgesIn(entry.before)
  const after = edgesIn(entry.after)
  const was = new Set(before.map(edgeId))
  const now = new Set(after.map(edgeId))
  return {
    added: after.filter((edge) => !was.has(edgeId(edge))),
    removed: before.filter((edge) => !now.has(edgeId(edge))),
  }
}

/** Whether an entry is about a role - its target, or a role named anywhere in its two snapshots. */
export function mentionsRole(entry: AuditEntry, roleKey: string): boolean {
  if (roleKey === '') return true
  if (entry.targetType === 'role' && entry.targetKey === roleKey) return true
  const quoted = `"${roleKey}"`
  return JSON.stringify(entry.before ?? null).includes(quoted) || JSON.stringify(entry.after ?? null).includes(quoted)
}

/**
 * Whether the change was made by somebody other than a main administrator - *len zmeny od vedúcich*.
 *
 * The Control Tower (`platform`) is not a delegate, and an entry from before §10A.408 carries no role and is not one
 * either: unknown is not *a shift lead did this*.
 */
export function isFromDelegate(entry: AuditEntry): boolean {
  const role = entry.actorRole ?? null
  return role !== null && role !== '' && role !== 'main_admin' && role !== 'platform'
}

export interface AuditFilter {
  /** A role key, or `''` for all. */
  role: string
  /** A target employee's id as the trail stores it (`targetKey`), or `''`. */
  person: string
  /** An actor's name, or `''`. */
  actor: string
  delegatesOnly: boolean
}

/** The client-side filters over the 200 entries already read - design §2.9. */
export function filterAudit(entries: readonly AuditEntry[], filter: AuditFilter): AuditEntry[] {
  return entries.filter((entry) => {
    if (!mentionsRole(entry, filter.role)) return false
    if (filter.person !== '' && !(entry.targetType === 'employee' && entry.targetKey === filter.person)) return false
    if (filter.actor !== '' && (entry.actorName ?? '') !== filter.actor) return false
    if (filter.delegatesOnly && !isFromDelegate(entry)) return false
    return true
  })
}

/** Who an entry was done by, as a role: *Hlavný administrátor*, a custom role's name, or the Control Tower. */
export function actorRoleText(entry: AuditEntry, nameOf: (key: string) => string, platform: string): string | null {
  const role = entry.actorRole ?? null
  if (role === null || role === '') return null
  if (role === 'platform') return platform
  return nameOf(role)
}

/**
 * What a trail entry's target is called: a role by its name, anything else by the name the trail stored - and **the
 * delegation graph by nothing**: §10A.408 writes it as `targetType: 'delegations'`, `targetKey: 'graph'` and no name,
 * and *graph* is a key nobody on the staff reads, while the action already says what was changed (*zmenil, kto spravuje
 * ktoré role*).
 */
export function targetText(entry: AuditEntry, nameOf: (key: string) => string): string {
  if (entry.targetType === 'delegations') return ''
  if (entry.targetType === 'role' && entry.targetKey !== undefined) {
    // A built-in role's entry often stores its key as the name; the label is what a reader knows it by.
    const stored = text(entry.targetName)
    return stored !== null && stored !== entry.targetKey ? stored : nameOf(entry.targetKey)
  }
  return entry.targetName ?? entry.targetKey ?? ''
}

/** A field `update_role` records (§10A.408's table: *the fields that changed*). */
export type RoleField = 'name' | 'color' | 'description' | 'sortOrder'

export interface RoleFieldChange {
  field: RoleField
  /** The value as the reader reads it - a colour by its name, nothing as null. */
  from: string | null
  to: string | null
}

const ROLE_FIELDS: readonly RoleField[] = ['name', 'color', 'description', 'sortOrder']

function fieldValue(field: RoleField, value: unknown): string | null {
  if (field === 'color' && typeof value === 'string' && isRoleColor(value)) return roleColorLabel(value)
  if (typeof value === 'number' && Number.isFinite(value)) return formatCount(value)
  return text(value)
}

/**
 * An `update_role` entry as the fields it changed, each *z … na …* - so a rename reads as a rename rather than as *Bez
 * zmeny v právach*, which is true and says nothing. Null for any other entry; a field neither snapshot names is not
 * listed.
 */
export function roleFieldChangesOf(entry: AuditEntry): RoleFieldChange[] | null {
  if (entry.action !== 'update_role') return null
  const before = record(entry.before) ?? {}
  const after = record(entry.after) ?? {}
  return ROLE_FIELDS.filter((field) => field in before || field in after).map((field) => ({
    field,
    from: fieldValue(field, before[field]),
    to: fieldValue(field, after[field]),
  }))
}

/** A changed field's name, as the rename form labels it. */
export function roleFieldLabel(field: RoleField): string {
  switch (field) {
    case 'name':
      return tt('Názov')
    case 'color':
      return tt('Farba')
    case 'description':
      return tt('Popis')
    case 'sortOrder':
      return tt('Poradie')
  }
}

export interface RoleDeletion {
  /** Where its people went, or null when it had none. */
  movedTo: string | null
  moved: number
  signedOut: number
}

/**
 * A `delete_role` entry's after-half - where the role's people went and how many of them that signed out
 * (`{movedTo, moved, signedOut}`). Null for any other entry.
 */
export function roleDeletionOf(entry: AuditEntry): RoleDeletion | null {
  if (entry.action !== 'delete_role') return null
  const after = record(entry.after) ?? {}
  const count = (value: unknown) => (typeof value === 'number' && Number.isFinite(value) ? value : 0)
  return { movedTo: text(after.movedTo), moved: count(after.moved), signedOut: count(after.signedOut) }
}
